import { useEffect, useRef, useState, useSyncExternalStore } from "react";

/**
 * Drawer giriş/çıkış geçişi — tüm drawer'ların ortak davranışı.
 *
 * İki sorunu birden çözer:
 *
 * 1. **"Tak diye açılma".** Drawer'lar `open` bayrağıyla AYNI commit'te
 *    mount oluyordu; eleman doğrudan açık pozisyonda (`translate-x-0`)
 *    doğduğu için tarayıcının animasyonlayacağı bir başlangıç durumu
 *    olmuyordu. Burada önce kapalı pozisyonda mount ediyoruz, iki frame
 *    sonra `entered` true'ya dönüyor — geçiş böylece gerçekten çalışıyor.
 *    (Tek frame yetmiyor: ilk rAF hâlâ aynı boyama turunda olabiliyor.)
 *
 * 2. **Kapanırken içeriğin sıfırlanması.** Kapatan taraf genelde
 *    `setOpen(false)` ile birlikte veriyi de temizliyor (device=null,
 *    adım=1, renk sıfırlanması...). Drawer hâlâ ekranda kaydığı için bu
 *    değişim kullanıcıya görünüyordu. `data` verilirse açıkken son değeri
 *    saklarız ve kapanış animasyonu boyunca onu döndürürüz.
 *
 * @param open  drawer açık mı
 * @param data  kapanış boyunca dondurulacak veri (opsiyonel)
 * @returns {{mounted: boolean, entered: boolean, data: any}}
 *          mounted → DOM'da dursun mu (çıkış animasyonu bitene kadar true)
 *          entered → açık pozisyon sınıfları uygulansın mı
 */
export const DRAWER_TRANSITION_MS = 300;

/*
 * Açık drawer sayacı — mobil alt menü (Sidebar) bunu dinler.
 *
 * Mobil alt gezinme çubuğu, açık dropdown'ların (z-[10000]) üstünde kalsın
 * diye z-[10100]'de duruyor; drawer'lar ise 9999 ve altında. Bu yüzden
 * mobilde alt menü drawer'ın alt kısmını (kaydet/aksiyon butonları) örtüyordu
 * (Client#104). Drawer'ı alt menünün üstüne çıkarmak, drawer içindeki
 * dropdown portal'larını drawer'ın altında bırakırdı. Çözüm: drawer DOM'da
 * durduğu sürece (çıkış animasyonu dahil) alt menü gizlenir. Tüm drawer'lar
 * (shared `Drawer` + özel paneller) bu hook'u kullandığı için kayıt tek
 * noktadan yapılır.
 */
let openDrawerCount = 0;
const drawerListeners = new Set();

function emitDrawerChange() {
  drawerListeners.forEach((listener) => listener());
}

function subscribeDrawerOpen(listener) {
  drawerListeners.add(listener);
  return () => drawerListeners.delete(listener);
}

function getAnyDrawerOpen() {
  return openDrawerCount > 0;
}

/** Herhangi bir drawer DOM'da mı (açık ya da kapanış animasyonunda)? */
export function useAnyDrawerOpen() {
  return useSyncExternalStore(subscribeDrawerOpen, getAnyDrawerOpen, getAnyDrawerOpen);
}

export default function useDrawerTransition(open, data = undefined) {
  const [mounted, setMounted] = useState(Boolean(open));
  const [entered, setEntered] = useState(false);
  const frozenRef = useRef(data);

  // Açıkken son veriyi sakla; kapanış boyunca bunu döndüreceğiz.
  useEffect(() => {
    if (open) frozenRef.current = data;
  }, [open, data]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = null;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        if (inner) cancelAnimationFrame(inner);
      };
    }
    setEntered(false);
    const timer = setTimeout(() => setMounted(false), DRAWER_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // DOM'da durduğu sürece "açık drawer" say → mobil alt menü gizlenir.
  useEffect(() => {
    if (!mounted) return undefined;
    openDrawerCount += 1;
    emitDrawerChange();
    return () => {
      openDrawerCount -= 1;
      emitDrawerChange();
    };
  }, [mounted]);

  return { mounted, entered, data: open ? data : frozenRef.current };
}
